"use client";

import { forwardRef, useState, type ComponentType, type InputHTMLAttributes } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "./Input";
import { cn } from "../../lib/utils";

// Passwortfeld mit Sichtbarkeits-Umschalter. EIN Ort fuer alle Passwortfelder der
// Suite — vorher stand in jedem Formular ein handgebautes `<Input type="password">`
// mit eigenem Wrapper, und nur `janeway-office-tools` hatte ueberhaupt ein Auge.
//
// `type` ist bewusst NICHT ueberschreibbar: es traegt hier den Zustand des
// Umschalters. Ein durchgereichtes `type` haette den Umschalter still tot gemacht.
//
// Der Knopf bleibt im Tab-Weg (kein `tabIndex={-1}`) — er ist eine echte
// Bedienung und muss ohne Maus erreichbar sein. Fuer die Enter-Bedienung des
// Formulars ist das folgenlos: `type="button"` loest kein Submit aus, und Enter
// im Eingabefeld erreicht den CTA unabhaengig von der Tab-Reihenfolge.

type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  /** Symbol links im Feld (z. B. `Lock`). Ohne Angabe bleibt links kein Platz frei. */
  icon?: ComponentType<{ size?: number; className?: string; "aria-hidden"?: boolean | "true" | "false" }>;
};

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, icon: Icon, ...props }, ref) => {
    const [sichtbar, setSichtbar] = useState(false);

    return (
      <div className="relative">
        {Icon && (
          <Icon
            className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground"
            size={15}
            aria-hidden="true"
          />
        )}
        <Input
          ref={ref}
          {...props}
          // NACH dem Spread — sonst gewinnt ein durchgereichtes `type` und der
          // Umschalter ist still wirkungslos. Das Typsystem allein reicht nicht:
          // ein `as`-Cast am Aufrufer kommt daran vorbei.
          type={sichtbar ? "text" : "password"}
          className={cn(Icon && "pl-9", "pr-10", className)}
        />
        <button
          type="button"
          onClick={() => setSichtbar((v) => !v)}
          aria-label={sichtbar ? "Passwort verbergen" : "Passwort anzeigen"}
          aria-pressed={sichtbar}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-[var(--radius)] text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {sichtbar ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = "PasswordInput";

export { PasswordInput, type PasswordInputProps };
